import React, { useEffect, useState } from 'react';

import Select, { OptionTypeBase, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';

type AsyncLoadingProps<T> = {
  loadOptions?: (inputValue: string) => Promise<T[]>;
};

type ClearableProps = {
  clearable: true;
  onSelect?: (id: string | undefined) => void;
};
type NotClearableProps = {
  clearable?: false;
  onSelect?: (id: string) => void;
};
export type AutoCompleteProps<T> = {
  className?: string;
  disabled?: boolean;
  initialValue?: T;
  initialId?: string;
} & (ClearableProps | NotClearableProps) &
  AsyncLoadingProps<T>;

type InternalProps<T> = {
  options?: T[];
  isLoading?: boolean;
  getOptionLabel: (item: T | undefined) => string;
  getOptionValue: (item: T) => string;
};

const AutoComplete = <T extends OptionTypeBase>(
  props: AutoCompleteProps<T> & InternalProps<T>
) => {
  const [value, setValue] = useState<T | undefined | null>(props.initialValue);

  useEffect(() => {
    if (props.options && props.initialId)
      setValue(props.options.find(opt => opt.id === props.initialId));
  }, [props.options]);

  const handleChange = (option: ValueType<T, false>) => {
    setValue(option);

    if (props.clearable) {
      if (props.onSelect) props.onSelect(option?.id);
    } else {
      if (!option) return;

      if (props.onSelect) props.onSelect(option.id);
    }
  };

  if (props.loadOptions) {
    return (
      <AsyncSelect
        loadOptions={props.loadOptions}
        className={props.className}
        isDisabled={props.disabled}
        getOptionLabel={props.getOptionLabel}
        getOptionValue={props.getOptionValue}
        openMenuOnFocus={false}
        value={value}
        onChange={handleChange}
        defaultOptions
      />
    );
  } else {
    return (
      <Select
        className={props.className}
        isDisabled={props.disabled}
        isSearchable={true}
        isLoading={props.isLoading}
        options={props.options}
        getOptionLabel={props.getOptionLabel}
        getOptionValue={props.getOptionValue}
        openMenuOnFocus={false}
        value={value}
        onChange={handleChange}
        isClearable={props.clearable || false}
      />
    );
  }
};

export default AutoComplete;
